import { Inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { HttpParams, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SharedService } from '@otas/shared/shared.service';
import { WINDOW } from 'app/services/window.provider';
import { AppConstants } from 'app/app.constants';
import { environment } from 'environments/environment';
import { GlobalUtilService } from '@otas/core/services/global-util.service';

@Injectable()
export class LoginStodaService {

  tuid?: string = null;
  /**
   * WS-DBM Phase 3: true once the `all` feed has confirmed this installation has
   * NO temple yet, so the caller routes to the first-run registration screen
   * instead of indexing an empty array (which used to throw).
   */
  unregistered = false;
  host: string;

  constructor(private http: HttpClient, private sharedService: SharedService,
    private globalUtilService: GlobalUtilService, @Inject(WINDOW) private window: Window) {
    this.host = this.domain_from_url(this.getHostname());
    this.tuid = localStorage.getItem('tuid');
  }
  getRoles(): Observable<any> {
    // const params = new HttpParams().append('permit', 'false');

    return this.http.get<any>('user/roles');//, { params: params });
  }
  login(object): Observable<any> {
    return this.http.post<any>('user/counter/login', object);
  }
  initialCall(): Observable<any> {
    return this.http.get<any>('all');
  }

  getVersionCheck(): Observable<boolean> {
    const hostType = AppConstants.checkHostType(this.host);
    console.log('getVersionCheck - hostType', hostType);
    const desktopLike = this.globalUtilService.isTauriApp || hostType === 'LOCALHOST' || hostType === 'IP';
    // R48: the cached-tuid short-circuit applies ONLY to the web-domain portal
    // flow. On the desktop/local flow it used to skip the `all` request
    // entirely once a machine had logged in ONCE - which silently disabled the
    // whole login server-status check (retry loop, circle, serverInitialize)
    // that the electron app ran unconditionally. Desktop always calls `all`:
    // it doubles as the reachability probe and refreshes the temple/pooja/
    // deity caches.
    if (!desktopLike && this.tuid) {
      return of(true);
    }
    if (desktopLike) {
      return this.http.get<any>('all').pipe(map((response) => this.applyTenantFeed(response)));
    } else {
      return this.http.get<{ temple: any[], pooja: any[], deity: any[] }>(`all/${this.host}/${this.tuid}`)
        .pipe(
          map((response) => {
            if (!!response) {
              const templeArray: any[] = response.temple;
              if (!templeArray || !templeArray.length) {
                this.unregistered = true;
                return false;
              }
              this.unregistered = false;
              const poojaList = response.pooja;
              const deityList = response.deity;
              this.tuid = templeArray[0].tuid ? templeArray[0].tuid : templeArray[0].code;
              localStorage.setItem('tuid', this.tuid);
              localStorage.setItem('templeHome', templeArray[0].homeUrl);
              localStorage.setItem('temple', JSON.stringify(templeArray));
              localStorage.setItem('pooja', JSON.stringify(poojaList));
              localStorage.setItem('deity', JSON.stringify(deityList));
              return true;
            }
            return false;
          })
        );
    }
  }

  /**
   * R51: resolve the tenant from an ALREADY-FETCHED `all` feed when one is at
   * hand. The login page's reachability probe downloads the complete tenant
   * payload on every attempt — re-requesting the same bytes a second time via
   * getVersionCheck() was the "same API called twice" the owner reported
   * (Angular never de-duplicates HTTP: every subscribe sends its own request).
   * On the desktop/local flow the probe hits the SAME endpoint with the same
   * inputs (`GET all`; the backend reads tuid only as a query param and
   * neither caller sends one), so the response is identical by construction
   * and is applied directly with ZERO extra HTTP. Without a prefetched feed —
   * and always on the web-domain portal flow, whose feed URL differs — this
   * falls back to the plain getVersionCheck() request.
   */
  resolveTenant(prefetchedFeed?: any): Observable<boolean> {
    const hostType = AppConstants.checkHostType(this.host);
    const desktopLike = this.globalUtilService.isTauriApp || hostType === 'LOCALHOST' || hostType === 'IP';
    if (desktopLike && prefetchedFeed) {
      return of(this.applyTenantFeed(prefetchedFeed));
    }
    return this.getVersionCheck();
  }

  /**
   * Applies a desktop `all` feed: resolves the tenant (tuid) and refreshes the
   * temple/pooja/deity localStorage caches. Flags `unregistered` on an empty
   * temple list (WS-DBM first-run flow).
   */
  applyTenantFeed(response: any): boolean {
    if (!!response) {
      const templeArray: any[] = response.temple || response.result || [];
      if (!templeArray.length) {
        // Fresh install: nothing to resolve a tenant from yet.
        this.unregistered = true;
        return false;
      }
      this.unregistered = false;
      const poojaList = response.pooja;
      const deityList = response.deity;
      this.tuid = templeArray[0].tuid ? templeArray[0].tuid : templeArray[0].code;
      localStorage.setItem('tuid', this.tuid);
      localStorage.setItem('templeHome', templeArray[0].homeUrl);
      localStorage.setItem('temple', JSON.stringify(templeArray));
      localStorage.setItem('pooja', JSON.stringify(poojaList));
      localStorage.setItem('deity', JSON.stringify(deityList));
      return true;
    }
    return false;
  }

  getHostname(): string {
    return this.window.location.hostname;
  }
  domain_from_url(url) {
    let result: string;
    let match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n\?\=]+)/im);
    if (!!match) {
      result = match[1];
      match = result.match(/^[^\.]+\.(.+\..+)$/);
      if (!!match) {
        result = match[1];
      }
    }
    return result;
  }

  getPoojas() {
    this.sharedService.getTemplePoojas();
  }

  getMaterials() {
    this.sharedService.getTempleMaterial();
  }

  getAllbankList(): Observable<any> {
    return this.http.get<any>(`account-head/bank`);
  }

  getApi(api): Observable<any> {
    return this.http.get<any>(api);
  }
}
